import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getSupabaseClient } from '../lib/supabase';

interface SeekerIntakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  seekerId?: string;
  seekerEmail?: string;
}

type IntakeRecord = Record<string, unknown>;

const DEFAULT_NOT_SPECIFIED = 'Not specified';
const DEFAULT_NOT_RECORDED = 'Not recorded';
const DEFAULT_NOT_AVAILABLE = 'Not available';

const parseJSONValue = <T,>(value: string): T | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed) as T;
  } catch (error) {
    return null;
  }
};

const normalizeArray = (value: unknown): string[] | null => {
  if (value === null || value === undefined) return null;

  const cleanArray = (input: unknown[]): string[] =>
    input
      .map((item) => (item === null || item === undefined ? '' : String(item)))
      .map((item) => item.trim())
      .filter(Boolean);

  if (Array.isArray(value)) {
    const normalized = cleanArray(value);
    return normalized.length ? normalized : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const jsonParsed = parseJSONValue<unknown[]>(trimmed);
    if (jsonParsed && Array.isArray(jsonParsed)) {
      const normalized = cleanArray(jsonParsed);
      return normalized.length ? normalized : null;
    }

    const sanitized = trimmed.replace(/^\{|\}$/g, '');
    const pieces = sanitized
      .split(',')
      .map((part) => part.replace(/^"+|"+$/g, '').trim())
      .filter(Boolean);

    if (pieces.length) {
      return pieces;
    }

    return [trimmed];
  }

  return null;
};

const normalizeObject = (value: unknown): Record<string, unknown> | null => {
  if (!value) return null;

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  if (typeof value === 'string') {
    const parsed = parseJSONValue<Record<string, unknown>>(value);
    if (parsed) {
      return parsed;
    }
  }

  return null;
};

const formatTextValue = (value: unknown, fallback: string = DEFAULT_NOT_SPECIFIED): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : fallback;
  }
  return String(value);
};

const formatArrayValue = (value: unknown, fallback: string = DEFAULT_NOT_SPECIFIED): string => {
  const normalized = normalizeArray(value);
  return normalized && normalized.length ? normalized.join(', ') : fallback;
};

const formatBooleanValue = (
  value: unknown,
  yesText: string = 'Yes',
  noText: string = 'No',
  fallback: string = DEFAULT_NOT_SPECIFIED,
): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'boolean') return value ? yesText : noText;

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return fallback;

  if (['yes', 'y', 'true', '1'].includes(normalized)) return yesText;
  if (['no', 'n', 'false', '0'].includes(normalized)) return noText;

  return fallback;
};

const formatDateValue = (value: unknown, fallback: string = DEFAULT_NOT_RECORDED): string => {
  if (value === null || value === undefined) return fallback;

  const dateString = typeof value === 'string' ? value : String(value);
  const parsed = new Date(dateString);

  if (Number.isNaN(parsed.getTime())) {
    return fallback;
  }

  return parsed.toLocaleString();
};

const formatFocusPreferences = (value: unknown): string => {
  const focusObject = normalizeObject(value);
  if (!focusObject) return DEFAULT_NOT_SPECIFIED;

  const segments = Object.entries(focusObject).reduce<string[]>((acc, [key, raw]) => {
    const normalized = normalizeArray(raw);
    if (!normalized || !normalized.length) return acc;

    const readableKey = key
      .replace(/^focus_/, '')
      .replace(/_pref$/, '')
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    acc.push(`${readableKey}: ${normalized.join(', ')}`);
    return acc;
  }, []);

  return segments.length ? segments.join(' | ') : DEFAULT_NOT_SPECIFIED;
};

const normalizeZip = (value: string): string | null => {
  if (!value) return null;
  const digitsOnly = value.replace(/[^0-9]/g, '');
  if (digitsOnly.length === 5) return digitsOnly;
  if (digitsOnly.length === 9) return digitsOnly.slice(0, 5);
  return null;
};

const useZippopotamus = (postcode: string | null) => {
  const [location, setLocation] = useState<string | null>(null);

  useEffect(() => {
    if (!postcode) {
      setLocation(null);
      return;
    }
    let cancelled = false;
    fetch(`https://api.zippopotam.us/US/${postcode}`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (cancelled || !data || !data.places || !data.places.length) return;
        const place = data.places[0];
        setLocation(`${place['place name']}, ${place['state']} (${postcode})`);
      })
      .catch(() => {
        if (!cancelled) setLocation(null);
      });
    return () => { cancelled = true; };
  }, [postcode]);
  return location;
};

const formatLocationValue = (value: unknown): string => {
  const raw = formatTextValue(value, '').trim();
  if (!raw) return DEFAULT_NOT_SPECIFIED;
  const candidateZip = normalizeZip(raw);
  // This will be replaced by the hook in the component
  return candidateZip || raw;
};

interface SectionField {
  label: string;
  value: string;
  emphasis?: boolean;
}

interface Section {
  title: string;
  fields: SectionField[];
}

const SeekerIntakeModal: React.FC<SeekerIntakeModalProps> = ({ isOpen, onClose, seekerId, seekerEmail }) => {
  const [intakeData, setIntakeData] = useState<IntakeRecord | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    setError(null);

    const fetchIntake = async () => {
      try {
        const supabase = getSupabaseClient();
        let query = supabase
          .from('seeker_onboarding_complete')
          .select('*')
          .order('onboarding_completed_at', { ascending: false })
          .limit(1);

        if (seekerEmail) {
          query = query.eq('seeker_email', seekerEmail);
        } else if (seekerId) {
          query = query.eq('seeker_id', seekerId);
        }

        const { data, error: supabaseError } = await query;

        if (supabaseError) {
          setIntakeData(null);
          setError(supabaseError.message ?? 'Unable to load intake data.');
          return;
        }

        if (!data || data.length === 0) {
          setIntakeData(null);
          return;
        }

        setIntakeData(data[0] ?? null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load intake data.';
        setError(message);
        setIntakeData(null);
      } finally {
        setLoading(false);
      }
    };

    void fetchIntake();
  }, [isOpen, seekerEmail, seekerId]);

  // Location lookup via Zippopotam.us (hook must be top-level)
  const candidateZip = intakeData ? normalizeZip(intakeData.location_postcode as string) : null;
  const locationLabel = useZippopotamus(candidateZip);

  const sections = useMemo<Section[]>(() => {
    if (!intakeData) return [];

    const overviewFields: SectionField[] = [
      { label: 'Over 18', value: formatBooleanValue(intakeData.seeker_over_18) },
      { label: 'Gender', value: formatTextValue(intakeData.seeker_gender) },
      { label: 'Location', value: locationLabel ? locationLabel : formatTextValue(intakeData.location_postcode) },
    ];

    const surveyFields: SectionField[] = [
      { label: 'Primary Goals', value: formatFocusPreferences(intakeData.focus_preferences) },
      { label: 'Experience Level', value: formatArrayValue(intakeData.experience_types, 'No prior experience specified') },
      { label: 'Preferred Setting', value: formatArrayValue(intakeData.support_types) },
      { label: 'Medical Conditions', value: formatArrayValue(intakeData.clinical_diagnoses, 'None reported') },
      { label: 'Medicine Comfort Level', value: formatArrayValue(intakeData.medicine_options_comfort) },
      { label: 'Travel Willingness', value: formatArrayValue(intakeData.travel_willingness) },
      { label: 'Travel Distance', value: formatTextValue(intakeData.travel_distance) },
      { label: 'Specialist Groups Preference', value: formatArrayValue(intakeData.prefer_specialist_in_groups) },
      { label: 'Religion Alignment Important', value: formatBooleanValue(intakeData.religion_alignment_important) },
      { label: 'Beliefs', value: formatTextValue(intakeData.seeker_beliefs, 'None provided') },
      { label: 'Currently on Medication', value: formatBooleanValue(intakeData.currently_on_meds) },
      { label: 'Budget Band', value: formatTextValue(intakeData.budget_band) },
      { label: 'Additional Information', value: formatTextValue(intakeData.additional_info, 'None provided') },
    ];

    const onboardingFields: SectionField[] = [
      { label: 'Onboarding Status', value: formatTextValue(intakeData.onboarding_status) },
      { label: 'Onboarding Started', value: formatDateValue(intakeData.onboarding_started_at) },
      { label: 'Onboarding Completed', value: formatDateValue(intakeData.onboarding_completed_at) },
    ];

    const matchFields: SectionField[] = [
      { label: 'Match Score', value: formatTextValue(intakeData.latest_match_score, DEFAULT_NOT_AVAILABLE) },
      { label: 'Match Reasoning', value: formatTextValue(intakeData.latest_match_reasoning, DEFAULT_NOT_AVAILABLE) },
      { label: 'Match Created', value: formatDateValue(intakeData.latest_match_created_at, DEFAULT_NOT_AVAILABLE) },
    ];

    const hasMatchDetails = matchFields.some((field) => field.value !== DEFAULT_NOT_AVAILABLE);

    const assembled: Section[] = [
      { title: 'Seeker Overview', fields: overviewFields },
      { title: 'Survey Results', fields: surveyFields },
      { title: 'Onboarding', fields: onboardingFields },
    ];

    if (hasMatchDetails) {
      assembled.push({ title: 'Latest Match', fields: matchFields });
    }

    return assembled;
  }, [intakeData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="flex w-full max-w-3xl max-h-[90vh] flex-col overflow-hidden rounded-2xl border border-[#2C2731]/30 bg-[#FFFCEF] shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#2C2731]/20 px-6 py-4">
          <h2 className="text-xl font-semibold text-[#2C2731]">Seeker Intake</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-[#2C2731]/70 transition hover:bg-[#F2EAC3] hover:text-[#2C2731]"
            aria-label="Close seeker intake modal"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-[#FFFCEF] px-6 py-6">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex min-h-[200px] items-center justify-center text-sm text-gray-500">
              Loading intake data…
            </div>
          ) : intakeData ? (
            <div className="flex flex-col gap-6">
              {sections.map((section) => (
                <div key={section.title} className="rounded-xl border border-[#2C2731]/15 bg-[#FFFCEF] p-5 shadow-sm">
                  <h3 className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-[#2C2731]/70">
                    {section.title}
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {section.fields.map((field) => {
                      if(field.label === 'Match Score') return null;
                      const valueClasses = field.emphasis
                        ? 'text-base font-medium text-gray-900'
                        : 'text-sm text-gray-900';

                      return (
                        <div key={`${section.title}-${field.label}`} className="flex flex-col gap-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]/50">
                            {field.label}
                          </span>
                          <span className={`whitespace-pre-line leading-5 ${valueClasses} text-[#2C2731]`}>
                            {field.value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-dashed border-[#2C2731]/30 bg-[#FFFCEF] px-6 py-12 text-sm text-[#2C2731]/60">
              No intake data found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeekerIntakeModal;
